import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
	ChevronRight,
	ChevronDown,
	Folder,
	FolderOpen,
	File,
	FileText,
	FileImage,
	FileCode,
	RefreshCw,
	Minus,
	Maximize2,
} from "lucide-react";
import { browseVault, type BrowseEntry, type BrowseListing } from "../../lib/api";
import styles from "./FilesPanel.module.css";

/**
 * Vault file browser panel rendered inside the Sidebar when Files mode is active.
 *
 * Design choices:
 *
 * - Root is ``root_dir`` — see every file, including untouched user notes.
 * - Lazy-loaded. Each directory fetches its children only when expanded for
 *   the first time; subsequent toggles just show/hide cached children.
 * - Purely client-side state. Expansion state and the in-memory listing cache
 *   don't persist across page reloads.
 * - Clicking a file **navigates to ``/files?path=<file>``** — the full file
 *   workspace with its preview canvas. Quick scanning from any page, deep
 *   reading on the dedicated page. One source of truth (the URL) for the
 *   selected file; the sidebar's selection highlight follows the URL.
 */
export function FilesPanel() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();

	// cache: logical path ("" = root) → entries
	const [cache, setCache] = useState<Record<string, BrowseEntry[]>>({});
	const [expanded, setExpanded] = useState<Set<string>>(new Set([""]));
	const [loading, setLoading] = useState<Set<string>>(new Set());
	const [errored, setErrored] = useState<Set<string>>(new Set());
	const treeRef = useRef<HTMLDivElement>(null);

	// Selected path comes from the URL when we're on /files?path=X.
	const selectedPath = (() => {
		if (location.pathname !== "/files") return "";
		const sp = new URLSearchParams(location.search);
		return sp.get("path") || "";
	})();

	const load = useCallback(async (path: string) => {
		setLoading(prev => { const s = new Set(prev); s.add(path); return s; });
		setErrored(prev => { const s = new Set(prev); s.delete(path); return s; });
		try {
			const listing: BrowseListing = await browseVault(path);
			setCache(prev => ({ ...prev, [path]: listing.entries }));
		} catch {
			setErrored(prev => { const s = new Set(prev); s.add(path); return s; });
		} finally {
			setLoading(prev => { const s = new Set(prev); s.delete(path); return s; });
		}
	}, []);

	// Load root on mount.
	useEffect(() => {
		if (!(ROOT_PATH in cache) && !loading.has(ROOT_PATH)) {
			void load(ROOT_PATH);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Expand ancestor directories when URL deep-links a file so the
	// selection is visible in the sidebar tree too.
	useEffect(() => {
		if (!selectedPath) return;
		const parts = selectedPath.split("/").filter(Boolean);
		const ancestors: string[] = [""];
		let acc = "";
		for (let i = 0; i < parts.length - 1; i++) {
			acc = acc ? `${acc}/${parts[i]}` : (parts[i] ?? "");
			ancestors.push(acc);
		}
		setExpanded(prev => {
			const s = new Set(prev);
			ancestors.forEach(a => s.add(a));
			return s;
		});
		ancestors.forEach(a => {
			if (!(a in cache) && !loading.has(a)) {
				void load(a);
			}
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedPath]);

	// Scroll the selected file into view whenever it changes OR the
	// tree's contents change (i.e. a lazy-loaded ancestor just finished
	// fetching). Without this, deep-linking to a nested file expands
	// the chain correctly but the highlighted row can be hidden below
	// the fold, forcing the user to hunt for it.
	useEffect(() => {
		if (!selectedPath || !treeRef.current) return;
		// `cache` dependency ensures we re-run AFTER the missing ancestor
		// listings have rendered their file rows.
		const selector = `[data-file-path="${CSS.escape(selectedPath)}"]`;
		const el = treeRef.current.querySelector<HTMLElement>(selector);
		if (!el) return;
		// rAF so we scroll after layout has settled (avoids scrolling
		// to a row that's still being positioned by sibling expansion).
		const id = window.requestAnimationFrame(() => {
			el.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });
		});
		return () => window.cancelAnimationFrame(id);
	}, [selectedPath, cache]);

	const toggle = useCallback(
		(path: string) => {
			setExpanded(prev => {
				const s = new Set(prev);
				if (s.has(path)) {
					s.delete(path);
				} else {
					s.add(path);
					if (!(path in cache) && !loading.has(path)) {
						void load(path);
					}
				}
				return s;
			});
		},
		[cache, loading, load],
	);

	const openFile = useCallback(
		(filePath: string, openInNewTab = false) => {
			const url = `/files?path=${encodeURIComponent(filePath)}`;
			if (openInNewTab) {
				// ``noopener`` + ``noreferrer`` for safety; ``_blank`` leaves
				// the current tab undisturbed so the user can compare two
				// files side-by-side without losing context.
				window.open(url, "_blank", "noopener,noreferrer");
			} else {
				navigate(url);
			}
		},
		[navigate],
	);

	const openWorkspace = useCallback(() => {
		navigate("/files");
	}, [navigate]);

	const refreshAll = useCallback(() => {
		setCache({});
		setErrored(new Set());
		const toReload = Array.from(expanded);
		toReload.forEach(p => void load(p));
	}, [expanded, load]);

	const collapseAll = useCallback(() => {
		setExpanded(new Set([""]));
	}, []);

	/**
	 * Keyboard navigation for the tree. Follows the WAI-ARIA tree pattern
	 * where it matters for a vault browser; skips the more elaborate
	 * multi-select / type-ahead parts that don't fit here.
	 *
	 * - ↑ / ↓        move focus to prev / next visible row
	 * - ← on open dir   collapse
	 * - ← on closed dir / file   move focus to parent dir
	 * - → on closed dir  expand
	 * - → on open dir    move focus to first child
	 * - Enter / Space    activate (toggle dir, open file)
	 * - Home / End       first / last visible row
	 *
	 * All actions operate on whatever row currently has DOM focus — so
	 * users can tab into the tree, then drive it entirely from the keyboard.
	 * Events bubble up from the nested button rows; we match on
	 * ``[data-row-path]`` to filter to actual tree rows.
	 */
	const onTreeKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLDivElement>) => {
			// Ignore modifiers the browser owns (Ctrl+F, Alt+←, etc.).
			if (e.ctrlKey || e.metaKey || e.altKey) return;

			const treeEl = treeRef.current;
			if (!treeEl) return;
			const active = document.activeElement;
			if (!(active instanceof HTMLElement)) return;
			const rowPath = active.dataset.rowPath;
			if (rowPath === undefined) return; // focus isn't on a row

			const rows = Array.from(
				treeEl.querySelectorAll<HTMLElement>("[data-row-path]"),
			);
			const idx = rows.indexOf(active);
			if (idx < 0) return;

			const focusRow = (next: HTMLElement | null | undefined) => {
				if (next) {
					next.focus();
					next.scrollIntoView({ block: "nearest", inline: "nearest" });
				}
			};

			switch (e.key) {
				case "ArrowDown": {
					e.preventDefault();
					focusRow(rows[idx + 1]);
					break;
				}
				case "ArrowUp": {
					e.preventDefault();
					focusRow(rows[idx - 1]);
					break;
				}
				case "Home": {
					e.preventDefault();
					focusRow(rows[0]);
					break;
				}
				case "End": {
					e.preventDefault();
					focusRow(rows[rows.length - 1]);
					break;
				}
				case "Enter":
				case " ": {
					e.preventDefault();
					active.click();
					break;
				}
				case "ArrowRight": {
					e.preventDefault();
					if (active.dataset.rowKind === "dir") {
						if (active.dataset.open === undefined) {
							// Closed → expand.
							toggle(rowPath);
						} else {
							// Open → move to first child row (the next row in DOM order).
							focusRow(rows[idx + 1]);
						}
					}
					break;
				}
				case "ArrowLeft": {
					e.preventDefault();
					if (active.dataset.rowKind === "dir" && active.dataset.open !== undefined) {
						// Open dir → collapse.
						toggle(rowPath);
					} else {
						// File or closed dir → jump to parent dir's row.
						const parentPath = rowPath.includes("/")
							? rowPath.slice(0, rowPath.lastIndexOf("/"))
							: "";
						const parentEl = treeEl.querySelector<HTMLElement>(
							`[data-row-path="${CSS.escape(parentPath)}"][data-row-kind="dir"]`,
						);
						if (parentEl) focusRow(parentEl);
					}
					break;
				}
				default:
					break;
			}
		},
		[toggle],
	);

	return (
		<div className={styles.panel}>
			<div className={styles.header}>
				<button
					type="button"
					className={styles.headerLabelBtn}
					onClick={openWorkspace}
					title={t("files.openWorkspace", "Open in Files workspace")}
				>
					<span className={styles.headerLabel}>{t("files.title", "Files")}</span>
					<Maximize2 size={11} className={styles.headerOpenIcon} />
				</button>
				<div className={styles.headerActions}>
					<button
						type="button"
						className={styles.iconBtn}
						onClick={collapseAll}
						aria-label={t("files.collapseAll", "Collapse all")}
						title={t("files.collapseAll", "Collapse all")}
					>
						<Minus size={14} />
					</button>
					<button
						type="button"
						className={styles.iconBtn}
						onClick={refreshAll}
						aria-label={t("files.refresh", "Refresh")}
						title={t("files.refresh", "Refresh")}
					>
						<RefreshCw size={14} />
					</button>
				</div>
			</div>
			<div className={styles.tree} role="tree" ref={treeRef} onKeyDown={onTreeKeyDown}>
				<DirectoryNode
					path={ROOT_PATH}
					name={t("files.root", "Vault root")}
					depth={0}
					isRoot={true}
					selectedPath={selectedPath}
					expanded={expanded}
					cache={cache}
					loading={loading}
					errored={errored}
					onToggle={toggle}
					onOpenFile={openFile}
				/>
			</div>
		</div>
	);
}

const ROOT_PATH = "";

interface DirectoryNodeProps {
	path: string;
	name: string;
	depth: number;
	isRoot?: boolean;
	selectedPath: string;
	expanded: Set<string>;
	cache: Record<string, BrowseEntry[]>;
	loading: Set<string>;
	errored: Set<string>;
	onToggle: (path: string) => void;
	onOpenFile: (path: string, openInNewTab?: boolean) => void;
}

function DirectoryNode({
	path,
	name,
	depth,
	isRoot = false,
	selectedPath,
	expanded,
	cache,
	loading,
	errored,
	onToggle,
	onOpenFile,
}: DirectoryNodeProps) {
	const { t } = useTranslation();
	const isOpen = expanded.has(path);
	const entries = cache[path];
	const isLoading = loading.has(path);
	const isErrored = errored.has(path);

	return (
		<div className={styles.node} role="treeitem" aria-expanded={isOpen}>
			<button
				type="button"
				className={`${styles.row} ${styles.dirRow} ${isRoot ? styles.rootRow : ""}`}
				style={{ paddingLeft: 6 + depth * 12 }}
				onClick={() => onToggle(path)}
				aria-expanded={isOpen}
				data-row-path={path}
				data-row-kind="dir"
				data-open={isOpen || undefined}
			>
				{isOpen ? (
					<ChevronDown size={12} className={styles.chev} />
				) : (
					<ChevronRight size={12} className={styles.chev} />
				)}
				{isOpen ? (
					<FolderOpen size={14} className={styles.folderIcon} />
				) : (
					<Folder size={14} className={styles.folderIcon} />
				)}
				<span className={styles.name}>{name}</span>
			</button>
			{isOpen && (
				<div className={styles.children}>
					{isLoading && !entries && (
						<div className={styles.muted} style={{ paddingLeft: 20 + depth * 12 }}>
							{t("files.loading", "Loading…")}
						</div>
					)}
					{isErrored && (
						<div className={styles.error} style={{ paddingLeft: 20 + depth * 12 }}>
							{t("files.errorLoad", "Could not load folder")}
						</div>
					)}
					{entries && entries.length === 0 && !isLoading && (
						<div className={styles.muted} style={{ paddingLeft: 20 + depth * 12 }}>
							{t("files.empty", "This folder is empty")}
						</div>
					)}
					{entries &&
						entries.map(entry => (
							<EntryNode
								key={entry.path}
								entry={entry}
								depth={depth + 1}
								selectedPath={selectedPath}
								expanded={expanded}
								cache={cache}
								loading={loading}
								errored={errored}
								onToggle={onToggle}
								onOpenFile={onOpenFile}
							/>
						))}
				</div>
			)}
		</div>
	);
}

interface EntryNodeProps {
	entry: BrowseEntry;
	depth: number;
	selectedPath: string;
	expanded: Set<string>;
	cache: Record<string, BrowseEntry[]>;
	loading: Set<string>;
	errored: Set<string>;
	onToggle: (path: string) => void;
	onOpenFile: (path: string, openInNewTab?: boolean) => void;
}

function EntryNode({
	entry,
	depth,
	selectedPath,
	expanded,
	cache,
	loading,
	errored,
	onToggle,
	onOpenFile,
}: EntryNodeProps) {
	if (entry.kind === "dir") {
		return (
			<DirectoryNode
				path={entry.path}
				name={entry.name}
				depth={depth}
				selectedPath={selectedPath}
				expanded={expanded}
				cache={cache}
				loading={loading}
				errored={errored}
				onToggle={onToggle}
				onOpenFile={onOpenFile}
			/>
		);
	}
	const isSelected = entry.path === selectedPath;
	// The suppression: treeitem is an INTERACTIVE role (ARIA 1.2) — biome
	// 1.9.4's classification is conservative. This row lives inside the
	// role="tree" container above, which owns the arrow-key navigation;
	// removing the role would break the tree widget's contract.
	return (
		// biome-ignore lint/a11y/noInteractiveElementToNoninteractiveRole: treeitem is interactive; part of the role="tree" widget
		<button
			type="button"
			className={`${styles.row} ${styles.fileRow} ${entry.is_hidden ? styles.hiddenEntry : ""} ${isSelected ? styles.fileSelected : ""}`}
			style={{ paddingLeft: 24 + depth * 12 }}
			// Ctrl/⌘+click OR middle-click → open in a new tab, matching
			// every other file browser + browser link convention.
			onClick={e => onOpenFile(entry.path, e.ctrlKey || e.metaKey)}
			onAuxClick={e => {
				if (e.button === 1) {
					e.preventDefault();
					onOpenFile(entry.path, true);
				}
			}}
			role="treeitem"
			aria-selected={isSelected}
			aria-current={isSelected ? "page" : undefined}
			data-file-path={entry.path}
			data-selected={isSelected || undefined}
			data-row-path={entry.path}
			data-row-kind="file"
			title={`${entry.path} · ${formatSize(entry.size)}`}
		>
			<FileTypeIcon name={entry.name} />
			<span className={styles.name}>{entry.name}</span>
			<span className={styles.size}>{formatSize(entry.size)}</span>
		</button>
	);
}

function FileTypeIcon({ name }: { name: string }) {
	const ext = name.toLowerCase().split(".").pop() || "";
	if (["md", "markdown", "txt", "log", "rst"].includes(ext))
		return <FileText size={13} className={styles.fileIconText} />;
	if (["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg", "ico"].includes(ext))
		return <FileImage size={13} className={styles.fileIconImage} />;
	if (
		[
			"py", "js", "ts", "tsx", "jsx", "json", "yaml", "yml", "toml", "sh",
			"rs", "go", "rb", "java", "c", "cpp", "h", "css", "html", "xml",
		].includes(ext)
	)
		return <FileCode size={13} className={styles.fileIconCode} />;
	return <File size={13} className={styles.fileIconGeneric} />;
}

function formatSize(bytes: number): string {
	if (bytes === 0) return "";
	if (bytes < 1024) return `${bytes} B`;
	if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
	if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
	return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

// Exported so it can be reused in tests.
export const __testing = { formatSize };
